/**
 * Impact — the evidence, measured against published benchmarks.
 *
 * Was /case-studies (which now redirects here). It leads with the cohort
 * evidence from the seed deck, from siteFacts, so the site and the deck give
 * one answer to "what is your engagement rate". The three program stories
 * first published on trust.care follow; their figures are what each program
 * reported against its own population, and they are labelled that way so they
 * are not read as competing with the cohort numbers.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import ProofStrip from '@/components/marketing/ProofStrip';
import ReachBenchmark from '@/components/marketing/ReachBenchmark';
import {
  ER_REDUCTION,
  LANGUAGE_EQUITY,
  PROOF_STRIP,
  contactHref,
  type Metric,
} from '@/components/marketing/siteFacts';

interface CaseStudy {
  slug: string;
  title: string;
  lede: string;
  body: string[];
  metrics: { value: string; label: string }[];
  source: string;
}

const CASE_STUDIES: CaseStudy[] = [
  {
    slug: 'medi-cal-enrollment-older-adults',
    title: 'Medi-Cal Enrollment for Older Adults',
    lede: 'Reaching older adults who were eligible for Medi-Cal but had not enrolled.',
    body: [
      'WeaveIn partnered with CCHI, whose initiative network spans 2,600+ community-based organizations, to increase Medi-Cal enrollment among older adults.',
      'Multilingual outreach in 110+ languages drove engagement 35% higher than the program\u2019s traditional methods.',
      'The program connected members to social services including food assistance, housing support and transportation.',
    ],
    metrics: [
      { value: '110+', label: 'Languages' },
      { value: '35%', label: 'Higher than the program\u2019s prior outreach' },
      { value: '2,600+', label: 'Organizations in the initiative network' },
    ],
    source: 'First published on trust.care',
  },
  {
    slug: 'calaim-community-centered-engagement',
    title: 'CalAIM Community-Centered Engagement',
    lede: 'Helping managed care plans reach members for ECM and Community Supports.',
    body: [
      "WeaveIn's community-first approach helped Managed Care Plans reach 45% engagement among the members referred into their Enhanced Care Management (ECM) and Community Supports programs.",
      'Closed-loop referrals connected members to housing navigation, medically tailored meals and post-hospitalization recuperative care.',
      'Participating health plans reported a 259% return on investment.',
    ],
    metrics: [
      { value: '45%', label: 'Engagement among referred members' },
      { value: '259%', label: 'ROI reported by participating plans' },
      { value: 'Closed-loop', label: 'Referral tracking' },
    ],
    source: 'First published on trust.care',
  },
  {
    slug: 'platform-expansion-and-retention',
    title: 'Platform Expansion and Member Retention',
    lede: 'What happened after organizations went live with their first program.',
    body: [
      '80% of CalAIM ECM and Community Supports users expanded platform usage beyond their initial program within two months.',
      '100% of managed care sponsors now run multiple programs through WeaveIn.',
      'The result was a 40% reduction in member churn and improvements in HEDIS quality scores.',
    ],
    metrics: [
      { value: '40%', label: 'Churn reduction reported' },
      { value: '80%', label: 'Expanded usage' },
      { value: '100%', label: 'Run multiple programs' },
    ],
    source: 'First published on trust.care',
  },
];

const Callout: React.FC<{ m: Metric }> = ({ m }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
    <div className="text-4xl font-bold text-ink">{m.value}</div>
    <div className="font-semibold text-gray-900 dark:text-gray-100 mt-1">{m.label}</div>
    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{m.definition}</p>
  </div>
);

const ImpactPage: React.FC = () => {
  usePageMeta(SEO_ROUTES['/impact']);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <MarketingHeader />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-3">Impact</p>
        <h1 className="text-3xl md:text-5xl font-bold text-ink mb-4 leading-tight">
          Measured, and reproducible.
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl">
          Real Medi-Cal outreach across four community-organization federations, measured against published benchmarks from
          KFF, DHCS, CMS, CHCS and Pew.
        </p>
      </section>

      <ReachBenchmark heading="Every federation clears the benchmark by 6–13×" />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Callout m={LANGUAGE_EQUITY} />
        <Callout m={ER_REDUCTION} />
      </section>

      <ProofStrip showEngagement={false} labels={PROOF_STRIP.slice(1).map((m) => m.label)} />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">Program stories</h2>
        <p className="text-base text-gray-600 dark:text-gray-300 max-w-3xl">
          Three programs, the numbers each one reported against its own population, and where the results were first
          published.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-8">
        {CASE_STUDIES.map((study) => (
          <article
            key={study.slug}
            className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 md:p-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-ink mb-2">
              {study.title}
            </h2>
            <p className="text-base md:text-lg text-gray-700 dark:text-gray-200 font-medium mb-6">
              {study.lede}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {study.metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 p-4"
                >
                  <div className="text-2xl font-bold text-v2accent">{metric.value}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 mb-6">
              {study.body.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 40)}
                  className="text-base text-gray-600 dark:text-gray-300 leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <p className="flex items-center text-xs text-gray-400 dark:text-gray-500">
              <Quote className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
              {study.source}
            </p>
          </article>
        ))}
      </section>

      <section className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">
            Want to see it with your own programs?
          </h2>
          <p className="text-base text-gray-600 dark:text-gray-300 mb-6">
            Walk through a live encounter from your own programs, then start a pilot with one program and a defined
            population.
          </p>
          <Link
            to={contactHref('general', 'walkthrough')}
            className="inline-flex items-center px-6 py-3 rounded-lg bg-ink text-paper text-sm font-medium hover:bg-ink-700"
          >
            Book a walkthrough
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
};

export default ImpactPage;
